/**
 * SY-02.2 privacy region. A server-provided country (CDN geo header) wins;
 * otherwise the device time zone decides. Ambiguous zones resolve to the
 * stricter regime: showing a consent tool where it is not needed is safe,
 * skipping it where it is needed is not.
 */
import type { Region } from '../schemas.js';

export type { Region };

/** EU member states plus the other EEA states (GDPR applies). */
const EEA = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
]);
/** UK GDPR and its equivalents: the UK, the Crown Dependencies and Gibraltar. */
const UK = new Set(['GB', 'UK', 'GG', 'JE', 'IM', 'GI']);
const UK_ZONES = new Set(['Europe/London', 'Europe/Belfast', 'Europe/Guernsey', 'Europe/Jersey', 'Europe/Isle_of_Man', 'Europe/Gibraltar', 'GB', 'GB-Eire']);
/** European zones outside the EEA and UK. */
const NON_EEA_EUROPE = new Set([
  'Europe/Moscow', 'Europe/Kirov', 'Europe/Volgograd', 'Europe/Samara', 'Europe/Ulyanovsk', 'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Kaliningrad', 'Europe/Minsk',
  'Europe/Istanbul', 'Europe/Kiev', 'Europe/Kyiv', 'Europe/Simferopol', 'Europe/Uzhgorod', 'Europe/Zaporozhye', 'Europe/Chisinau', 'Europe/Tiraspol',
  'Europe/Belgrade', 'Europe/Sarajevo', 'Europe/Skopje', 'Europe/Podgorica', 'Europe/Tirane', 'Europe/Zurich', 'Europe/Andorra', 'Europe/Monaco', 'Europe/San_Marino', 'Europe/Vatican',
]);
const EEA_OUTSIDE_EUROPE_ZONES = new Set(['Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Reykjavik', 'Atlantic/Faroe', 'Africa/Ceuta', 'Arctic/Longyearbyen', 'Asia/Nicosia', 'Asia/Famagusta']);
/** Pacific time covers California; the zone cannot tell CA from WA/OR/NV, so it counts as us-ca. */
const CA_ZONES = new Set(['America/Los_Angeles', 'US/Pacific', 'PST8PDT']);

export interface RegionInput {
  /** Intl.DateTimeFormat().resolvedOptions().timeZone */
  timeZone?: string | null;
  /** ISO 3166-1 alpha-2 from the server/CDN, if known */
  country?: string | null;
  /** ISO 3166-2 subdivision code without country, e.g. 'CA' */
  subdivision?: string | null;
}

export function regionFromTimeZone(tz: string): Region {
  if (UK_ZONES.has(tz)) return 'uk';
  if (EEA_OUTSIDE_EUROPE_ZONES.has(tz)) return 'eu';
  if (tz.startsWith('Europe/') && !NON_EEA_EUROPE.has(tz)) return 'eu';
  if (CA_ZONES.has(tz)) return 'us-ca';
  return 'other';
}

export function resolveRegion(i: RegionInput): Region {
  const country = i.country?.toUpperCase();
  if (country) {
    if (UK.has(country)) return 'uk';
    if (EEA.has(country)) return 'eu';
    if (country === 'US') {
      if (i.subdivision) return i.subdivision.toUpperCase() === 'CA' ? 'us-ca' : 'other';
      return i.timeZone && CA_ZONES.has(i.timeZone) ? 'us-ca' : 'other';
    }
    return 'other';
  }
  return i.timeZone ? regionFromTimeZone(i.timeZone) : 'other';
}

export const deviceTimeZone = (): string | null => Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;

/** opt-in: nothing tracked before a choice (GDPR/UK GDPR). opt-out: tracking allowed, "do not sell/share" offered (CPRA). */
export type ConsentModel = 'opt-in' | 'opt-out' | 'none';

export function consentModel(r: Region): ConsentModel {
  return r === 'eu' || r === 'uk' ? 'opt-in' : r === 'us-ca' ? 'opt-out' : 'none';
}

export function requiresConsentBeforeTracking(r: Region): boolean {
  return consentModel(r) === 'opt-in';
}

/** The certified consent tool is shown in the EU, UK and California (SY-02.2). */
export function showsConsentTool(r: Region): boolean {
  return consentModel(r) !== 'none';
}
